import { Link, useLinkState, useRoute } from "eddev/routing"
import { SectionNavFragment } from "../../types.graphql"
import clsx from "clsx"
import { AnimatePresence, LayoutGroup, motion } from "framer-motion"
import { Fragment, PropsWithChildren, ReactNode, useEffect, useId } from "react"
import { Button } from "@components/controls/Button"

export type SectionNavItem = {
  label?: string | null | ReactNode
  url?: string | null
  children?: (SectionNavItem | null)[] | null
}

type Props = {
  nav?: SectionNavItem
}

export function SectionNavSidebar(props: Props) {
  return <RecursiveItems children={props.nav?.children! as any} root />
}

export function SectionNavTopBar(props: Props) {
  const route = useRoute()
  const key = useId()

  return (
    <LayoutGroup id={key}>
      <motion.nav className="w-grid mx-auto sm:hidden py-3 -my-3" layoutRoot layoutScroll={false}>
        <ul className="flex gap-2 flex-wrap">
          {props.nav?.children?.map((child) => {
            return (
              <Fragment key={child?.url}>
                <motion.li layoutId={child?.url!} className="type-ui-m-light">
                  <Button href={child?.url} size="chip" variant="lightLink">
                    {child?.label}
                  </Button>
                </motion.li>
                {!!child?.url &&
                  route.pathname.replace(/\/?$/, "/").startsWith(child?.url.replace(/\/?$/, "/")) &&
                  child?.children?.map((child) => {
                    return (
                      <motion.li key={child?.url} layoutId={child?.url!} className="type-ui-m-light">
                        <Button href={child?.url} size="chip" variant="outline">
                          {child?.label}
                        </Button>
                      </motion.li>
                    )
                  })}
              </Fragment>
            )
          })}
        </ul>
      </motion.nav>
    </LayoutGroup>
  )
}

function RecursiveItems(props: { children?: SectionNavFragment[]; root?: boolean }) {
  if (!props.children || props.children.length === 0) {
    return null
  }
  return (
    <ul className="el-ul:pl-4 el-ul:pt-2">
      {props.children.map((child, i) => {
        return (
          <li key={child.url} className="type-ui-m-light pb-[0.5em]">
            <Link
              href={child.url}
              className={clsx(
                "link-active:decoration-fg/40 decoration-fg/0 underline underline-offset-[0.1em] transition-colors",
                props.root && i === 0 ? "" : "link-child-active:decoration-fg/40"
              )}
            >
              {child.label}
            </Link>
            <ExpandableNav link={child.url ?? "/"}>
              <RecursiveItems children={child.children as any} />
            </ExpandableNav>
          </li>
        )
      })}
    </ul>
  )
}

function ExpandableNav(props: PropsWithChildren<{ link: string }>) {
  const state = useLinkState(props.link)

  return (
    <AnimatePresence>
      {(state.active || state.childActive) && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
          {props.children}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
